import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useLocation } from 'react-router-dom';

import { ItemCardLiveBidButton as ItemCardBidButton } from '@/components/GridItemCard/Card/components/ItemCardBidButton/ItemCardLiveBidButton';
import ItemCardEstimate from '@/components/GridItemCard/Card/components/ItemCardEstimate/ItemCardEstimate';
import ItemCardPriceRowSearchCard from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRowSearchCard';
import PriceRowSearchCard from '@/components/ItemCard/PriceRowSearchCard';
import ItemPriceGatewayContainer from '@/components/ItemPriceGateway/ItemPriceGatewayContainer';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import {
    getIsRegistered,
    getRegistrationApproved,
    getRegistrationApprovedWithLimit,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getIsItemSummaryTimedPlusBiddingOpen,
    getItemSummaryCatalogId,
    getItemSummaryIsPassed,
    getItemSummaryIsPaused,
    getItemSummaryIsSold,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { openRegisterForAuctionModal } from '@/redux/modules/modal';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import { TimedPlusStatus } from '@/types/TimedPlusStatus.enum';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { isRegisterButtonHiddenPage } from '@/utils/urls';

export type RegisterLinkOrPriceSearchCardProps = {
    itemId: number;
    linkHover?: boolean;
    onNavigate?: () => void;
    onRegisterForAuctionClicked?: () => void;
};

const RegisterLinkOrPriceSearchCard = ({
    itemId,
    linkHover,
    onNavigate,
    onRegisterForAuctionClicked,
}: RegisterLinkOrPriceSearchCardProps) => {
    const { pathname } = useLocation();
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();
    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isPaused = useAppSelector((state) => getItemSummaryIsPaused(state, itemId));
    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const approvedWithBidLimit = useAppSelector((state) => getRegistrationApprovedWithLimit(state, catalogId));
    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));
    const isLargeScreen = useIsWiderThan(mergedCaterwaulAndHammerUIThemes.breakpoints.mobileWidthNum);

    const isHiddenMenuPage = pathname.includes('/catalog/') || pathname.includes('/item/');
    const isHiddenButtonPage = isRegisterButtonHiddenPage(pathname);

    const isTimedPlusBiddingOpen = useAppSelector((state) => getIsItemSummaryTimedPlusBiddingOpen(state, itemId));

    const registerForAuctionLabel = formatMessage([
        'itemPage',
        'biddingInfo',
        'timedPlus',
        TimedPlusStatus.NotStarted,
        'registerForAuction',
    ]);

    let content: JSX.Element;

    if (isSold) {
        content = isLoggedIn ? (
            <div className="flex justify-between [grid-area:price]">
                <ItemCardPriceRowSearchCard
                    isHovered={linkHover}
                    itemId={itemId}
                    onNavigate={onNavigate}
                />
            </div>
        ) : (
            <ItemPriceGatewayContainer
                className="[&_button]:text-link text-base [&_button]:no-underline [&:hover_button]:underline"
                hideIcon
                itemId={itemId}
                loggedOutMessageId="price_gateway_realized_price"
            />
        );
    } else {
        const isOpenItem = !isPassed && !isPaused && isTimedPlusBiddingOpen;
        const isApprovedForOpenItem = isOpenItem && (approved || approvedWithBidLimit);
        const needsToRegister = !isPassed && !registered && !isHiddenMenuPage;

        let userActionButtonOrLink: JSX.Element | undefined;
        if (isApprovedForOpenItem) {
            userActionButtonOrLink = <ItemCardBidButton itemId={itemId} />;
        } else if (needsToRegister) {
            userActionButtonOrLink = isLargeScreen ? (
                <Button
                    className="w-full"
                    onClick={() => {
                        onRegisterForAuctionClicked?.();
                        dispatch(
                            openRegisterForAuctionModal({
                                catalogId,
                                itemId,
                            })
                        );
                    }}
                    size="sm"
                    style={isOpenItem ? 'critical' : 'primary'}
                >
                    {registerForAuctionLabel}
                </Button>
            ) : (
                <InlineButton
                    onClick={() => {
                        dispatch(
                            openRegisterForAuctionModal({
                                catalogId,
                                itemId,
                            })
                        );
                    }}
                >
                    {registerForAuctionLabel}
                </InlineButton>
            );
        }
        content = (
            <div className="flex flex-col">
                <ItemCardEstimate
                    className={cn('text-xs smMin:text-sm')}
                    itemId={itemId}
                />
                <PriceRowSearchCard
                    className="mt-sm!"
                    isCatalogCard
                    itemId={itemId}
                    onNavigate={onNavigate}
                />
                {!isHiddenButtonPage && <div className="flex h-full items-end">{userActionButtonOrLink}</div>}
            </div>
        );
    }

    return <div className="relative h-full [grid-area:registerlinkorprice]">{content}</div>;
};

export default RegisterLinkOrPriceSearchCard;
